import { useEffect, useMemo, useRef } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { TextField } from '@mui/material';
import * as Yup from 'yup';
import { Dialog } from '@components/Dialog/Dialog';
import { Button } from '@components/buttons/Button';
import { SecureSubmitButton } from '@components/buttons/SecureSubmitButton';
import { useStore } from '@stores/RootStore';
import type { TenantSsoProviderModel } from '@repositories/tenantSsoProviderRepository/models/TenantSsoProviderModel';
import { useUpdateSsoProvider } from '@queryHooks/useTenantSsoProvider';
import type { SsoProviderFormData } from './SsoProviderStyles';
import {
  FormBody,
  FieldsContainer,
  FieldRow,
  RequiredLabel,
  OptionalLabel,
  ModalButtonBox,
} from './SsoProviderStyles';

type Props = {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  providerId: string;
  provider: TenantSsoProviderModel;
};

export const SsoProviderEditModal = ({ open, onClose, tenantId, providerId, provider }: Props) => {
  const { t } = useTranslation();
  const {
    uiStore: { toastStore },
  } = useStore();

  const { mutate: updateProvider, isPending } = useUpdateSsoProvider();
  const prevOpenRef = useRef(false);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        providerName: Yup.string().required(t('SsoProvider.create.providerNameRequired')).max(255).trim(),
        issuerUri: Yup.string().required(t('SsoProvider.create.issuerUriRequired')).url(t('SsoProvider.create.invalidUrl')).trim(),
        clientId: Yup.string().required(t('SsoProvider.create.clientIdRequired')).trim(),
        clientSecret: Yup.string().trim(),
        redirectUri: Yup.string()
          .matches(/^https?:\/\/.+/, { message: t('SsoProvider.create.invalidUrl'), excludeEmptyString: true })
          .trim(),
        scopes: Yup.string().trim(),
        userNameAttribute: Yup.string().trim(),
        userInfoUri: Yup.string()
          .matches(/^https?:\/\/.+/, { message: t('SsoProvider.create.invalidUrl'), excludeEmptyString: true })
          .trim(),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SsoProviderFormData>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  // 모달이 열리는 순간에만 기존 데이터로 폼 초기화 (백그라운드 refetch 시 입력 보존)
  useEffect(() => {
    const isOpening = open && !prevOpenRef.current;
    prevOpenRef.current = open;

    if (isOpening) {
      reset({
        providerName: provider.providerName,
        issuerUri: provider.issuerUri,
        clientId: provider.clientId,
        clientSecret: '',
        redirectUri: provider.redirectUri,
        scopes: provider.scopes,
        userNameAttribute: provider.userNameAttribute,
        userInfoUri: provider.userInfoUri,
      });
    }
  }, [open, provider, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleValid: SubmitHandler<SsoProviderFormData> = formData => {
    const trimmedSecret = formData.clientSecret?.trim();

    updateProvider(
      {
        tenantId,
        providerId,
        dto: {
          providerName: formData.providerName.trim(),
          issuerUri: formData.issuerUri.trim(),
          clientId: formData.clientId.trim(),
          // clientSecret이 비어 있으면 DTO에서 제외 (백엔드가 기존 값 유지)
          ...(trimmedSecret && { clientSecret: trimmedSecret }),
          redirectUri: formData.redirectUri?.trim() || undefined,
          scopes: formData.scopes?.trim() || undefined,
          userNameAttribute: formData.userNameAttribute?.trim() || undefined,
          userInfoUri: formData.userInfoUri?.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          toastStore.open(t('SsoProvider.edit.success'), 'bottom', 'center');
          handleClose();
        },
        onError: () => {
          toastStore.open(t('SsoProvider.edit.fail'), 'bottom', 'center');
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} title={t('SsoProvider.edit.title')} size="medium" useCustomContent>
      <FormBody onSubmit={handleSubmit(handleValid)}>
        <FieldsContainer>
          <FieldRow>
            <RequiredLabel>{t('SsoProvider.create.providerName')}</RequiredLabel>
            <TextField
              {...register('providerName')}
              placeholder={t('SsoProvider.create.providerNamePlaceholder')}
              size="small"
              fullWidth
              error={!!errors.providerName}
              helperText={errors.providerName?.message}
            />
          </FieldRow>
          <FieldRow>
            <RequiredLabel>{t('SsoProvider.create.issuerUri')}</RequiredLabel>
            <TextField
              {...register('issuerUri')}
              placeholder="https://example.okta.com/oauth2/default"
              size="small"
              fullWidth
              error={!!errors.issuerUri}
              helperText={errors.issuerUri?.message}
            />
          </FieldRow>
          <FieldRow>
            <RequiredLabel>{t('SsoProvider.create.clientId')}</RequiredLabel>
            <TextField
              {...register('clientId')}
              placeholder={t('SsoProvider.create.clientIdPlaceholder')}
              size="small"
              fullWidth
              error={!!errors.clientId}
              helperText={errors.clientId?.message}
            />
          </FieldRow>
          <FieldRow>
            <OptionalLabel>{t('SsoProvider.create.clientSecret')}</OptionalLabel>
            <TextField
              {...register('clientSecret')}
              placeholder={t('SsoProvider.edit.clientSecretPlaceholder')}
              size="small"
              fullWidth
              type="password"
              error={!!errors.clientSecret}
              helperText={errors.clientSecret?.message}
            />
          </FieldRow>
          <FieldRow>
            <OptionalLabel>{t('SsoProvider.create.redirectUri')}</OptionalLabel>
            <TextField
              {...register('redirectUri')}
              placeholder="https://example.com/login"
              size="small"
              fullWidth
              error={!!errors.redirectUri}
              helperText={errors.redirectUri?.message}
            />
          </FieldRow>
          <FieldRow>
            <OptionalLabel>{t('SsoProvider.create.scopes')}</OptionalLabel>
            <TextField
              {...register('scopes')}
              placeholder="openid,profile,email"
              size="small"
              fullWidth
              error={!!errors.scopes}
              helperText={errors.scopes?.message}
            />
          </FieldRow>
          <FieldRow>
            <OptionalLabel>{t('SsoProvider.create.userNameAttribute')}</OptionalLabel>
            <TextField
              {...register('userNameAttribute')}
              placeholder="email"
              size="small"
              fullWidth
              error={!!errors.userNameAttribute}
              helperText={errors.userNameAttribute?.message}
            />
          </FieldRow>
          <FieldRow>
            <OptionalLabel>{t('SsoProvider.create.userInfoUri')}</OptionalLabel>
            <TextField
              {...register('userInfoUri')}
              placeholder="https://example.okta.com/oauth2/default/v1/userinfo"
              size="small"
              fullWidth
              error={!!errors.userInfoUri}
              helperText={errors.userInfoUri?.message}
            />
          </FieldRow>
        </FieldsContainer>
        <ModalButtonBox>
          <SecureSubmitButton isPending={isPending} type="submit" variant="contain" size="extraLarge">
            {t('SsoProvider.edit.submit')}
          </SecureSubmitButton>
          <Button variant="outline" size="extraLarge" onClick={handleClose}>
            {t('Cancel')}
          </Button>
        </ModalButtonBox>
      </FormBody>
    </Dialog>
  );
};
